import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 32. NOTES
// Notes internes rattachables à une entité (client, entreprise,
// commande, projet, réservation, professionnel…).
// Les notes privées ne doivent JAMAIS être exposées à l'AI Client.
// ─────────────────────────────────────────────────────────────

export const NOTE_ENTITY_TYPES: { key: string; label: string; icon: string }[] = [
  { key: 'customer', label: 'Client', icon: 'ri-user-star-line' },
  { key: 'business', label: 'Entreprise', icon: 'ri-building-2-line' },
  { key: 'order', label: 'Commande', icon: 'ri-shopping-bag-3-line' },
  { key: 'project', label: 'Projet', icon: 'ri-folders-line' },
  { key: 'reservation', label: 'Réservation', icon: 'ri-calendar-check-line' },
  { key: 'professional', label: 'Professionnel', icon: 'ri-user-2-line' },
  { key: 'contact', label: 'Contact', icon: 'ri-contacts-line' },
  { key: 'ticket', label: 'Ticket', icon: 'ri-coupon-3-line' },
];

export function noteEntityMeta(key: string | null | undefined) {
  return NOTE_ENTITY_TYPES.find((e) => e.key === key) || null;
}

export const NOTE_VISIBILITIES: { key: string; label: string; icon: string; className: string; desc: string }[] = [
  {
    key: 'internal',
    label: 'Interne',
    icon: 'ri-team-line',
    className: 'bg-secondary-100 text-secondary-900',
    desc: 'Visible par l’équipe, utilisable par les modules internes.',
  },
  {
    key: 'private',
    label: 'Privée',
    icon: 'ri-lock-line',
    className: 'bg-accent-100 text-accent-900',
    desc: 'Confidentielle — jamais exposée à l’AI Client.',
  },
];

export function noteVisibilityMeta(key: string | null | undefined) {
  return NOTE_VISIBILITIES.find((v) => v.key === key) || NOTE_VISIBILITIES[0];
}

export interface CoreNote {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  entity_type: string | null;
  entity_id: string | null;
  title: string | null;
  body: string | null;
  is_private: boolean | null;
  visibility: string | null;
  author_user_id: number | null;
  author_name: string | null;
  pinned: boolean | null;
  deleted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export async function listCoreNotes(
  organizationId: number,
  filter?: { entityType?: string; entityId?: string | number },
): Promise<CoreNote[]> {
  let query = supabase
    .from('core_notes')
    .select('*')
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .order('pinned', { ascending: false })
    .order('created_at', { ascending: false });
  if (filter?.entityType && filter?.entityId !== undefined) {
    query = query.eq('entity_type', filter.entityType).eq('entity_id', String(filter.entityId));
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreNote[];
}

export interface NoteInput {
  tenantId: number | null;
  organizationId: number;
  entityType?: string | null;
  entityId?: string | null;
  title: string;
  body: string;
  visibility?: string;
  authorUserId?: number | null;
  authorName?: string | null;
}

export async function createCoreNote(input: NoteInput): Promise<number | null> {
  const visibility = input.visibility || 'internal';
  const { data, error } = await supabase
    .from('core_notes')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      entity_type: input.entityType || null,
      entity_id: input.entityId || null,
      title: input.title || null,
      body: input.body,
      is_private: visibility === 'private',
      visibility,
      author_user_id: input.authorUserId ?? null,
      author_name: input.authorName ?? null,
      pinned: false,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreNote(
  id: number,
  patch: { title?: string; body?: string; visibility?: string; pinned?: boolean },
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.title !== undefined) payload.title = patch.title;
  if (patch.body !== undefined) payload.body = patch.body;
  if (patch.visibility !== undefined) {
    payload.visibility = patch.visibility;
    payload.is_private = patch.visibility === 'private';
  }
  if (patch.pinned !== undefined) payload.pinned = patch.pinned;
  const { error } = await supabase.from('core_notes').update(payload).eq('id', id);
  if (error) throw error;
}

export async function softDeleteCoreNote(id: number, userId?: number | null): Promise<void> {
  const { error } = await supabase
    .from('core_notes')
    .update({ deleted_at: new Date().toISOString(), deleted_by: userId ?? null })
    .eq('id', id);
  if (error) throw error;
}

export async function restoreCoreNote(id: number): Promise<void> {
  const { error } = await supabase
    .from('core_notes')
    .update({ deleted_at: null, deleted_by: null })
    .eq('id', id);
  if (error) throw error;
}

export async function toggleNotePin(id: number, pinned: boolean): Promise<void> {
  const { error } = await supabase.from('core_notes').update({ pinned }).eq('id', id);
  if (error) throw error;
}

/** Notes visibles par l'AI Client : uniquement les notes non privées. */
export async function listAiVisibleNotes(organizationId: number): Promise<CoreNote[]> {
  const { data, error } = await supabase
    .from('core_notes')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('visibility', 'internal')
    .eq('is_private', false)
    .is('deleted_at', null);
  if (error) throw error;
  return (data || []) as CoreNote[];
}

export interface NoteStats {
  total: number;
  privateCount: number;
  pinned: number;
  byEntityType: Record<string, number>;
}

export async function fetchNoteStats(organizationId: number): Promise<NoteStats> {
  const { data } = await supabase
    .from('core_notes')
    .select('entity_type, visibility, pinned')
    .eq('organization_id', organizationId)
    .is('deleted_at', null);
  const byEntityType: Record<string, number> = {};
  let privateCount = 0;
  let pinned = 0;
  (data || []).forEach((r) => {
    const row = r as { entity_type: string | null; visibility: string | null; pinned: boolean | null };
    const et = row.entity_type || 'general';
    byEntityType[et] = (byEntityType[et] || 0) + 1;
    if (row.visibility === 'private') privateCount += 1;
    if (row.pinned) pinned += 1;
  });
  return { total: (data || []).length, privateCount, pinned, byEntityType };
}